import { Space } from 'antd'
import { observer } from 'mobx-react-lite'
import { MdOutlineAttachFile } from 'react-icons/md'

import { FormField } from '@/Core/UI/Form'
import { BaseViewFieldTemplate } from '@/Ship/Templates/Form/BaseViewFieldTemplate'

import type { FC, ReactNode } from 'react'

/** Ship-шаблон — .jsx без типів; children у пропсах відновлюємо аліасом. */
const ViewFieldTemplate = BaseViewFieldTemplate as FC<{ field: { label: string }; children?: ReactNode }>

interface FileLike {
    uuid?: string
    originalName?: string | null
    url?: string | null
}

interface FormContextLike {
    isEditable?: boolean
    values?: Record<string, unknown>
}

export interface FileFieldCellProps {
    formContext: FormContextLike | undefined
    /** Ім'я файлового поля ресурсу */
    name: string
    /** Підпис — той самий, що в ресурсі */
    label: string
}

/**
 * Файлове поле в блоці картки: у формі — ядровий `FileInput`, у перегляді —
 * звичайне поле картки з переліком посилань або прочерком.
 *
 * Посилання — підписане, з терміном дії (ADR 042), і відкривається в новій
 * вкладці: картка лишається відкритою.
 *
 * Показу для файлового поля ядро не пускає (`view` ігнорується для
 * `type: 'file'`): і в перегляді воно малює той самий `FileInput` — з жирним
 * підписом форми замість підпису поля картки і без прочерку, коли файлів
 * немає.
 */
export const FileFieldCell = observer(({ formContext, name, label }: FileFieldCellProps) => {
    if (formContext?.isEditable === true) return <FormField name={name} />

    const raw = formContext?.values?.[name]
    // Поле на один файл (договір, Word документа) приходить об'єктом, а не масивом
    const files = (Array.isArray(raw) ? raw : raw !== null && typeof raw === 'object' ? [raw] : []) as FileLike[]

    return (
        <ViewFieldTemplate field={{ label }}>
            {files.length === 0 ? null : (
                <Space direction="vertical" size={4} style={{ display: 'flex' }}>
                    {files.map((file, index) => (
                        <Space key={file.uuid ?? index} size={6}>
                            <MdOutlineAttachFile />
                            {file.url ? (
                                <a href={file.url} target="_blank" rel="noreferrer">
                                    {file.originalName ?? 'файл'}
                                </a>
                            ) : (
                                (file.originalName ?? 'файл')
                            )}
                        </Space>
                    ))}
                </Space>
            )}
        </ViewFieldTemplate>
    )
})
